import { connect, useDispatch } from "react-redux";
import styles from "./style.module.scss";
import { SavedView } from "types";
import {
  getIsShareSavedViewEnabled,
  getSavedViews,
} from "redux/SingleDataset/selectors";
import { State } from "redux/store";
import { formatDateWithHours } from "helpers/utility/formatters";
import VLAvatar from "views/uikit/VLAvatar";
import { GoKebabHorizontal } from "react-icons/go";
import {
  MouseEvent as ReactMouseEvent,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DeleteViewModal } from "../DeleteViewModal";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { AMP_SAVED_VIEWS_EVENT__VIEW_EXPLORED } from "helpers/constants/amplitudeEvents";
import useNavigateToView from "hooks/useNavigateToView";
import { setShowViewSharingMenu } from "redux/SingleDataset/actions";

const ViewsActionsMenu = ({
  onExploreClick,
  onDeleteClick,
  onShareClick,
  actionsMenuRef,
  style,
  isShareSavedViewEnabled,
}: {
  onExploreClick: () => void;
  onDeleteClick: () => void;
  onShareClick: () => void;
  actionsMenuRef: Ref<HTMLDivElement>;
  style: { top: number; left: number };
  isShareSavedViewEnabled: boolean;
}) => {
  return (
    <div className={styles.actionsMenu} ref={actionsMenuRef} style={style}>
      <div className={styles.action} onClick={onExploreClick}>
        Explore
      </div>
      {isShareSavedViewEnabled && (
        <div className={styles.action} onClick={onShareClick}>
          Share
        </div>
      )}
      <div className={styles.action} onClick={onDeleteClick}>
        Delete
      </div>
    </div>
  );
};

interface SavedViewsTableProps {
  savedViews: SavedView[];
  isShareSavedViewEnabled: boolean;
}

const SavedViewsTable = ({
  savedViews,
  isShareSavedViewEnabled,
}: SavedViewsTableProps) => {
  const dispatch = useDispatch();
  const [actionsMenuView, setActionsMenuView] = useState<SavedView | null>(
    null
  );
  const [deleteModalView, setDeleteModalView] = useState<SavedView | null>(
    null
  );
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const navigateToView = useNavigateToView();
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleViewClick = (view: SavedView) => {
    amplitudeTrack(AMP_SAVED_VIEWS_EVENT__VIEW_EXPLORED);
    navigateToView(view);
  };

  const handleActionsMenuClick = (
    e: ReactMouseEvent<HTMLDivElement>,
    view: SavedView
  ) => {
    e.stopPropagation();
    const buttonRect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: buttonRect.top,
      left: buttonRect.left - buttonRect.width / 2,
    });
    setActionsMenuView((currentView) =>
      currentView?.id === view.id ? null : view
    );
  };

  const handleDeleteClick = (view: SavedView) => {
    setActionsMenuView(null);
    setDeleteModalView(view);
  };

  const handleShareClick = (view: SavedView) => {
    setActionsMenuView(null);
    dispatch(
      setShowViewSharingMenu({
        showSharingMenu: true,
        associatedViewId: view.id,
      })
    );
  };

  const handleCloseActionsMenu = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(e.target as Node)
      ) {
        setActionsMenuView(null);
      }
    },
    [actionsMenuRef]
  );

  useEffect(() => {
    document.addEventListener("click", handleCloseActionsMenu);
    return () => document.removeEventListener("click", handleCloseActionsMenu);
  }, [handleCloseActionsMenu]);

  return (
    <>
      <table className={styles.viewsTableContainer}>
        <thead className={styles.viewsTableHeaders}>
          <tr>
            <th>View Name</th>
            <th>Date Created</th>
            <th>Created By</th>
            <th className={styles.actionsColumn}>Actions</th>
          </tr>
        </thead>
        <tbody className={styles.viewsTableBody}>
          {savedViews.map((row: SavedView, index) => (
            <tr key={index} className={styles.row}>
              <td className={styles.title} onClick={() => handleViewClick(row)}>
                {row.name}
              </td>
              <td className={styles.createdAt}>
                {formatDateWithHours(row.createdAt)}
              </td>
              <td className={styles.createdBy}>
                <div className={styles.createdByAvatar}>
                  <VLAvatar
                    size="xsmall"
                    userAvatar={row.createdBy.avatar_uri}
                  />
                </div>
                <div className={styles.createdByLabels}>
                  <div>{row.createdBy.name || row.createdBy.email}</div>
                </div>
              </td>
              <td className={styles.actionsColumn}>
                <div
                  className={styles.actionsButtonContainer}
                  data-testid={`actions-button-${row.id}`}
                  onClick={(e: ReactMouseEvent<HTMLDivElement>) =>
                    handleActionsMenuClick(e, row)
                  }
                >
                  <GoKebabHorizontal />
                </div>
                {actionsMenuView?.id === row.id && menuPosition && (
                  <ViewsActionsMenu
                    actionsMenuRef={actionsMenuRef}
                    onDeleteClick={() => handleDeleteClick(row)}
                    onExploreClick={() => handleViewClick(row)}
                    onShareClick={() => handleShareClick(row)}
                    isShareSavedViewEnabled={isShareSavedViewEnabled}
                    style={{
                      top: menuPosition.top,
                      left: menuPosition.left,
                    }}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {deleteModalView && (
        <DeleteViewModal
          view={deleteModalView}
          onCloseModal={() => setDeleteModalView(null)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: State) => ({
  savedViews: getSavedViews(state),
  isShareSavedViewEnabled: getIsShareSavedViewEnabled(state),
});

export default connect(mapStateToProps)(SavedViewsTable);
